import React from 'react';
import { Check, AlertTriangle, Info } from 'lucide-react';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

/**
 * Parse a calendar URL and return the provider and embed URL.
 * Supports:
 *  - Google Calendar public/embed URLs
 *  - Outlook/Office 365 published calendar URLs
 */
export const parseCalendarUrl = (url) => {
  if (!url) return null;
  const trimmed = url.trim();

  // Google Calendar - already an embed URL (must start with http)
  if (/^https?:\/\/calendar\.google\.com\/calendar\/embed/i.test(trimmed)) {
    return { provider: 'google', embedUrl: trimmed };
  }

  // Google Calendar - public HTML URL → convert to embed (must start with http)
  // e.g. https://calendar.google.com/calendar/u/0?cid=BASE64
  if (/^https?:\/\/calendar\.google\.com/i.test(trimmed)) {
    const cidMatch = trimmed.match(/[?&]cid=([^&]+)/);
    if (cidMatch) {
      return {
        provider: 'google',
        embedUrl: `https://calendar.google.com/calendar/embed?src=${encodeURIComponent(cidMatch[1])}`,
      };
    }
    // Calendar ID in path (e.g. /calendar/ical/EMAIL/public/basic.ics)
    const emailMatch = trimmed.match(/\/calendar\/[^/]+\/([^/]+@[^/]+)/);
    if (emailMatch) {
      return {
        provider: 'google',
        embedUrl: `https://calendar.google.com/calendar/embed?src=${encodeURIComponent(emailMatch[1])}`,
      };
    }
    // If it's a google calendar URL but we can't parse it, pass through
    return { provider: 'google', embedUrl: trimmed };
  }

  // Google Calendar - raw calendar ID (user pastes their calendar ID)
  // Handles IDs like: en.usa#holiday@group.calendar.google.com or email@gmail.com
  if (/^[a-zA-Z0-9._%+#-]+@[a-zA-Z0-9.-]+$/i.test(trimmed)) {
    return {
      provider: 'google',
      embedUrl: `https://calendar.google.com/calendar/embed?src=${encodeURIComponent(trimmed)}`,
    };
  }

  // Outlook - published calendar URL (must start with http)
  if (/^https?:\/\/outlook\.(live|office365|office)\.com/i.test(trimmed)) {
    return { provider: 'outlook', embedUrl: trimmed };
  }

  return null;
};

const CalendarEmbedEditor = ({ block, onChange, theme, hideStylePanels }) => {
  const { content } = block;

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  const features = BLOCK_FEATURES['calendar-embed'];
  const calendarUrl = content.calendarUrl || '';
  const parsed = parseCalendarUrl(calendarUrl);
  const showValidation = calendarUrl.length > 0;
  const isValid = parsed !== null;

  return (
    <div className="space-y-4">
      {/* Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Heading (optional)
        </label>
        <input
          type="text"
          value={content.heading || ''}
          onChange={(e) => handleChange('heading', e.target.value)}
          placeholder="My Schedule"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Calendar URL */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Calendar URL
        </label>
        <div className="relative">
          <input
            type="text"
            value={calendarUrl}
            onChange={(e) => handleChange('calendarUrl', e.target.value)}
            placeholder="Paste your Google Calendar sharing URL"
            className={`w-full px-3 py-2 bg-[#1a1a2e] border rounded-lg text-white placeholder-gray-500 focus:outline-none pr-10 ${
              showValidation
                ? isValid
                  ? 'border-green-500 focus:border-green-500'
                  : 'border-red-500 focus:border-red-500'
                : 'border-gray-700 focus:border-[#749555]'
            }`}
          />
          {showValidation && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {isValid ? (
                <Check size={16} className="text-green-500" />
              ) : (
                <AlertTriangle size={16} className="text-red-500" />
              )}
            </div>
          )}
        </div>
        {showValidation && !isValid && (
          <p className="text-xs text-red-400 mt-1">
            Paste a Google Calendar sharing or embed URL
          </p>
        )}
        {showValidation && isValid && (
          <p className="text-xs text-green-400 mt-1">
            {parsed.provider === 'google' ? 'Google Calendar' : 'Outlook Calendar'} detected
          </p>
        )}
      </div>

      {/* Help text */}
      <div className="flex items-start gap-2 p-3 bg-[#1a1a2e] border border-gray-700 rounded-lg">
        <Info size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-gray-400 space-y-1">
          <p><strong className="text-gray-300">Google Calendar:</strong> Open Google Calendar &rarr; Settings &rarr; your calendar &rarr; "Integrate calendar" &rarr; copy the public URL or embed code URL.</p>
        </div>
      </div>

      {/* Google Calendar options */}
      {parsed?.provider === 'google' && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">Display Options</label>
          <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={content.showTitle !== false}
              onChange={(e) => handleChange('showTitle', e.target.checked)}
              className="rounded border-gray-600 bg-[#1a1a2e] text-[#749555] focus:ring-[#749555]"
            />
            Show calendar title
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={content.showNav !== false}
              onChange={(e) => handleChange('showNav', e.target.checked)}
              className="rounded border-gray-600 bg-[#1a1a2e] text-[#749555] focus:ring-[#749555]"
            />
            Show navigation buttons
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={content.showTabs !== false}
              onChange={(e) => handleChange('showTabs', e.target.checked)}
              className="rounded border-gray-600 bg-[#1a1a2e] text-[#749555] focus:ring-[#749555]"
            />
            Show view tabs (Week/Month/Agenda)
          </label>
        </div>
      )}

      {/* Style Panels */}
      {!hideStylePanels && (<>
        <SizeSpacingPanel
          style={content.style || {}}
          onChange={handleStyleChange}
          features={features}
        />
        <ColorsFontsPanel
          style={content.style || {}}
          onChange={handleStyleChange}
          features={features}
          theme={theme || {}}
          blockType="calendar-embed"
        />
      </>)}
    </div>
  );
};

export default CalendarEmbedEditor;
